/**
 * The language models the assistant can talk to, and how to talk to each.
 *
 * ## Bring your own key, and the key stays on the device
 *
 * There is no shared account and no server of ours in the path. The pilot pastes
 * their own key into Settings, the browser calls the model provider directly,
 * and the conversation never touches this project's backend. Three reasons, in
 * order of how much they matter:
 *
 *  1. **A key shipped inside a downloadable PWA is a public key.** Same
 *     reasoning as openAIP's and CARTO's, and the same answer.
 *  2. **Whose bill it is, is not ambiguous.** The pilot's key, the pilot's
 *     spend, the pilot's rate limit. Nobody's flight briefing is throttled
 *     because somebody else's was.
 *  3. **One fewer place the conversation exists.** A flight plan names where a
 *     pilot is going and when; routing it through a Lambda would put it in a
 *     log we would then have to be careful with. It does not go there at all.
 *
 * All four providers were checked from the production origin before a line of
 * the adapter was written — `OPTIONS` with the real `Access-Control-Request-*`
 * headers — because the registry's own header says a CORS claim is worthless
 * until it has been tested against the live service. All four answer with
 * `Access-Control-Allow-Origin` and allow the headers we send. Anthropic needs
 * one extra header to do it; see `ANTHROPIC_BROWSER_HEADER`.
 *
 * ## What is deliberately not here
 *
 * No local model, no default provider, and no free tier. The assistant is off
 * until a pilot chooses a provider and pastes a key: an EFB that quietly starts
 * spending somebody's money because a screen was opened would deserve
 * everything it got.
 *
 * Pure data. The wire protocols live in `services/assistant/providers/`.
 */

/**
 * How a provider is spoken to.
 *
 * Three shapes. `openai-chat` is the `/chat/completions` contract, which xAI and
 * OpenRouter implement deliberately and compatibly; `openai-responses` is
 * OpenAI's Responses API, which OpenAI itself needs because its reasoning models
 * refuse function tools on `/chat/completions` (owner's report, 2026-10-12 —
 * see `services/assistant/providers/openaiResponses.ts`); `anthropic-messages`
 * is the Messages API. Three adapters, four providers.
 */
export type AssistantWireFormat = 'openai-chat' | 'openai-responses' | 'anthropic-messages'

export interface AssistantModel {
	/** The provider's own model id, sent verbatim. */
	id: string
	/** What a pilot sees in the picker. */
	label: string
	/**
	 * Why this model is in the list.
	 *
	 * Shown under the picker. A pilot choosing between three names needs to know
	 * which one is worth the money for a briefing and which is worth it for a
	 * quick question, and no amount of model naming conveys that.
	 */
	noteKey: string
	/**
	 * Whether the model takes a photograph as input — what the logbook page
	 * reader needs (`core/logbook/photoImport/`). Every model in this catalogue
	 * is one of its vendor's multimodal models; a model typed into the free-text
	 * field is not in the catalogue, so for it the answer is unknown, and the
	 * reader tries it and says so when the provider refuses the picture.
	 */
	vision?: boolean
}

export interface AssistantProvider {
	id: string
	name: string
	operator: string
	wire: AssistantWireFormat
	baseUrl: string
	/**
	 * The key in `dataSources.apiKeys`, which is the same map every other
	 * provider's key lives in — so it is redacted from a share, kept in a
	 * backup, and synced only to the pilot's own partition, with no second
	 * mechanism to get wrong. See `core/preferences/registry.ts`.
	 */
	apiKeySettingKey: string
	/** The page that actually issues a key, not the API reference. */
	apiKeySignupUrl: string
	documentationUrl: string
	models: readonly AssistantModel[]
	/** Anything a pilot should know before pasting a key. A catalogue key. */
	caveatKey: string
}

/**
 * The header that makes Anthropic's API callable from a browser at all.
 *
 * Without it the request is refused with a CORS error that reads like a network
 * failure. The name is a warning about the usual case — a key embedded in a web
 * page is a leaked key — and it does not apply here: the key is the pilot's own,
 * typed on their own device, and never leaves it except to Anthropic.
 */
export const ANTHROPIC_BROWSER_HEADER = 'anthropic-dangerous-direct-browser-access'

/** The Messages API version this build is written against. */
export const ANTHROPIC_VERSION = '2023-06-01'

export const ASSISTANT_PROVIDERS: readonly AssistantProvider[] = Object.freeze([
	{
		id: 'anthropic',
		name: 'Claude (Anthropic)',
		operator: 'Anthropic PBC',
		wire: 'anthropic-messages',
		baseUrl: 'https://api.anthropic.com/v1',
		apiKeySettingKey: 'anthropicApiKey',
		apiKeySignupUrl: 'https://console.anthropic.com/settings/keys',
		documentationUrl: 'https://docs.claude.com/en/api/messages',
		// Anthropic's models overview, read 2026-10-12: Opus 5.5 is the one it
		// recommends for most work, Sonnet 5.5 the balance of speed and
		// intelligence, Haiku 5.5 the fastest. Every one supports tool use.
		models: [
			{
				id: 'claude-opus-5-5',
				label: 'Claude Opus 5.5',
				noteKey: 'assistant.models.deep',
				vision: true,
			},
			{
				id: 'claude-sonnet-5-5',
				label: 'Claude Sonnet 5.5',
				noteKey: 'assistant.models.balanced',
				vision: true,
			},
			{
				id: 'claude-haiku-5-5',
				label: 'Claude Haiku 5.5',
				noteKey: 'assistant.models.quick',
				vision: true,
			},
		],
		caveatKey: 'assistant.providers.anthropic.caveat',
	},
	{
		id: 'openai',
		name: 'ChatGPT (OpenAI)',
		operator: 'OpenAI',
		wire: 'openai-responses',
		baseUrl: 'https://api.openai.com/v1',
		apiKeySettingKey: 'openAiApiKey',
		apiKeySignupUrl: 'https://platform.openai.com/api-keys',
		documentationUrl: 'https://developers.openai.com/api/reference/resources/responses',
		// OpenAI's models page, read 2026-10-12: Astra the flagship, 6.1 Sol
		// "near-Astra performance at a lower cost", Luna the most efficient.
		// GPT-5.5 stays because pilots already use it.
		models: [
			{ id: 'gpt-6-astra', label: 'GPT-6 Astra', noteKey: 'assistant.models.deep', vision: true },
			{ id: 'gpt-6.1-sol', label: 'GPT-6.1 Sol', noteKey: 'assistant.models.balanced', vision: true },
			{ id: 'gpt-5.5', label: 'GPT-5.5', noteKey: 'assistant.models.balanced', vision: true },
			{ id: 'gpt-6-luna', label: 'GPT-6 Luna', noteKey: 'assistant.models.quick', vision: true },
		],
		caveatKey: 'assistant.providers.openai.caveat',
	},
	{
		id: 'xai',
		name: 'Grok (xAI)',
		operator: 'xAI',
		wire: 'openai-chat',
		baseUrl: 'https://api.x.ai/v1',
		apiKeySettingKey: 'xaiApiKey',
		apiKeySignupUrl: 'https://console.x.ai/',
		documentationUrl: 'https://docs.x.ai/docs/api-reference',
		// xAI's models page, read 2026-10-12.
		models: [
			{ id: 'grok-4.7', label: 'Grok 4.7', noteKey: 'assistant.models.deep', vision: true },
			{ id: 'grok-4.6', label: 'Grok 4.6', noteKey: 'assistant.models.balanced', vision: true },
			{ id: 'grok-4.3', label: 'Grok 4.3', noteKey: 'assistant.models.quick', vision: true },
		],
		caveatKey: 'assistant.providers.xai.caveat',
	},
	{
		id: 'openrouter',
		name: 'OpenRouter',
		operator: 'OpenRouter, Inc.',
		wire: 'openai-chat',
		baseUrl: 'https://openrouter.ai/api/v1',
		apiKeySettingKey: 'openRouterApiKey',
		apiKeySignupUrl: 'https://openrouter.ai/settings/keys',
		documentationUrl: 'https://openrouter.ai/docs/api-reference/overview',
		// One key, many models. The list below is a starting point and the model
		// field is editable, because the whole reason to use OpenRouter is to
		// reach something this list does not name.
		// Ids as OpenRouter's own `/models` lists them on 2026-10-12 — dots where
		// Anthropic's API uses dashes. Four vendors, so one key reaches a
		// different family when one is slow or down.
		models: [
			{
				id: 'anthropic/claude-sonnet-5.5',
				label: 'Claude Sonnet 5.5',
				noteKey: 'assistant.models.balanced',
				vision: true,
			},
			{
				id: 'openai/gpt-6.1-sol',
				label: 'GPT-6.1 Sol',
				noteKey: 'assistant.models.balanced',
				vision: true,
			},
			{ id: 'x-ai/grok-4.7', label: 'Grok 4.7', noteKey: 'assistant.models.deep', vision: true },
			{
				id: 'google/gemini-3.8-flash',
				label: 'Gemini 3.8 Flash',
				noteKey: 'assistant.models.quick',
				vision: true,
			},
		],
		caveatKey: 'assistant.providers.openrouter.caveat',
	},
])

export function assistantProvider(id: string | undefined | null): AssistantProvider | null {
	if (!id) return null
	return ASSISTANT_PROVIDERS.find((provider) => provider.id === id) ?? null
}

/** Every key the assistant providers use, for the "is anything configured" check. */
export function assistantKeySettingKeys(): string[] {
	return ASSISTANT_PROVIDERS.map((provider) => provider.apiKeySettingKey)
}

/**
 * Where a provider publishes the models a key may use.
 *
 * All four answer `GET /v1/models`, and the assistant offers a button that
 * reads it. That is deliberate: the list above is a starting point that will
 * be wrong within months — model names change faster than an app is
 * redeployed — and a picker that only offers stale names is a picker that
 * eventually offers nothing that works. The pilot's own key fetches the truth.
 */
export function modelsEndpoint(provider: AssistantProvider): string {
	return `${provider.baseUrl}/models`
}

/**
 * The model to use, given what the pilot chose.
 *
 * Falls back to the provider's first model rather than to a hard-coded id, so a
 * pilot who switches provider and forgets to pick a model gets that provider's
 * best one rather than a 404 from a model name that belongs to somebody else.
 * An unknown id is passed through untouched: OpenRouter's catalogue is far
 * larger than the entries above, and refusing a model the pilot typed
 * would make the free-text field a lie.
 */
export function resolveModelId(
	provider: AssistantProvider,
	chosen: string | undefined | null,
	nowMs: number = Date.now(),
): string {
	const wanted = (chosen ?? '').trim()
	if (!wanted) return provider.models[0]?.id ?? ''
	return replacementFor(provider.id, wanted, nowMs) ?? wanted
}

/**
 * Models their vendor has shut down, the day it does, and what it names instead.
 *
 * OpenAI, 2026-10-14: GPT-4o, GPT-4o mini, GPT-4.1 and GPT-4.1 mini shut down on
 * 2027-01-14, every snapshot, replacement `gpt-6-luna`
 * (developers.openai.com/api/docs/deprecations, read that day). A pilot who typed
 * one of them into the model field keeps it until then — it is their choice and
 * it still answers — and from that day the assistant asks the replacement
 * instead of failing every question with a 404 they would have to diagnose.
 */
export const RETIRED_MODELS: readonly {
	vendor: 'openai'
	ids: readonly string[]
	shutDownOn: string
	replacement: string
}[] = Object.freeze([
	{
		vendor: 'openai',
		ids: ['gpt-4o', 'gpt-4o-mini', 'gpt-4.1', 'gpt-4.1-mini'],
		shutDownOn: '2027-01-14',
		replacement: 'gpt-6-luna',
	},
])

/** `gpt-4o-2024-08-06` is `gpt-4o`; `gpt-4o-mini` is not `gpt-4o`. */
function isRetiredId(model: string, id: string): boolean {
	return (
		model === id || (model.startsWith(`${id}-`) && /^\d{4}-\d{2}-\d{2}$/.test(model.slice(id.length + 1)))
	)
}

/**
 * The model to ask instead of `model` at `providerId`, or null when it is not
 * retired yet. OpenRouter carries OpenAI's models as `openai/<id>`, retired on
 * the same day.
 */
export function replacementFor(providerId: string, model: string, nowMs: number = Date.now()): string | null {
	const routed = providerId === 'openrouter' && model.startsWith('openai/')
	const id = routed ? model.slice('openai/'.length) : model
	if (providerId !== 'openai' && !routed) return null
	for (const retirement of RETIRED_MODELS) {
		if (nowMs < Date.parse(`${retirement.shutDownOn}T00:00:00Z`)) continue
		if (retirement.ids.some((retired) => isRetiredId(id, retired))) {
			return routed ? `openai/${retirement.replacement}` : retirement.replacement
		}
	}
	return null
}

/**
 * Whether `model` at `provider` reads images: `true` for a catalogue model the
 * vendor publishes as multimodal, `null` for a model the catalogue does not
 * name — unknown, never assumed either way.
 */
export function modelReadsImages(provider: AssistantProvider, model: string): boolean | null {
	const entry = provider.models.find((candidate) => candidate.id === model)
	if (!entry) return null
	return entry.vision === true
}

/** The catalogue's image-reading models for a provider, best first. */
export function visionModels(provider: AssistantProvider): AssistantModel[] {
	return provider.models.filter((model) => model.vision === true)
}
